import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { SsnField } from "@/components/common/masked-form-field";
import { CheckboxField } from "@/features/clients/components/record-form-fields";
import { createDependent, updateDependent } from "@/features/worksheets/api";
import { CrudDialog } from "@/features/worksheets/components/crud-dialog";
import type { Dependent } from "@/features/worksheets/types";

interface Values {
  name: string;
  ssn: string;
  age: string;
  relationship: string;
  claimedOnReturn: boolean;
  contributesToHousehold: boolean;
}

const schema = Yup.object({
  name: Yup.string().trim().required("Name is required"),
  ssn: Yup.string().matches(/^(\d{3}-\d{2}-\d{4})?$/, "Enter a valid SSN"),
  age: Yup.string().matches(/^\d{0,3}$/, "Enter a valid age"),
});

interface Props {
  clientId: string;
  record: Dependent | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}

export function DependentDialog({ clientId, record, open, onOpenChange, onSaved }: Props) {
  const initialValues: Values = {
    name: record?.name ?? "",
    ssn: "", // never prefilled — the raw SSN is never returned (only a mask)
    age: record?.age != null ? String(record.age) : "",
    relationship: record?.relationship ?? "",
    claimedOnReturn: record?.claimedOnReturn ?? true,
    contributesToHousehold: record?.contributesToHousehold ?? false,
  };

  async function onSave(v: Values) {
    const payload: Record<string, unknown> = {
      name: v.name.trim(),
      age: v.age === "" ? null : Number(v.age),
      relationship: v.relationship,
      claimedOnReturn: v.claimedOnReturn,
      contributesToHousehold: v.contributesToHousehold,
    };
    // Only send the SSN if the user actually entered one, so editing without
    // re-typing it doesn't blank the stored value.
    if (v.ssn.trim()) payload.ssn = v.ssn;
    if (record) await updateDependent(clientId, record.id, payload);
    else await createDependent(clientId, payload);
  }

  return (
    <CrudDialog
      open={open}
      onOpenChange={onOpenChange}
      editing={record !== null}
      noun="Dependent"
      initialValues={initialValues}
      validationSchema={schema}
      onSave={onSave}
      onSaved={onSaved}
    >
      <FormField name="name" label="Name" />
      <div className="grid grid-cols-2 gap-4">
        <SsnField
          name="ssn"
          label="SSN"
          labelAdornment={
            record?.ssnLabel ? (
              <span className="text-muted-foreground text-xs">On file: {record.ssnLabel}</span>
            ) : undefined
          }
        />
        <FormField name="age" label="Age" inputMode="numeric" />
      </div>
      <FormField name="relationship" label="Relationship" placeholder="e.g. Child" />
      <CheckboxField name="claimedOnReturn" label="Claimed on tax return" />
      <CheckboxField name="contributesToHousehold" label="Contributes to household income" />
    </CrudDialog>
  );
}
